import { FC } from 'react';
import { capitalize } from 'lodash';
import SelectTime from 'pages/OfficeSchedulePage/DayTimeSlot/SelectTime/SelectTime';
import { ReactComponent as ClearIcon } from 'assets/icons/clear.svg';
import { useFormContext } from 'react-hook-form';
import { TimeFrame, TimeFrameFormValues } from '../AdvancedSetting';
import { DEFAULT_WORKING_HOUR } from 'utils/constants';
import OperatoryDropdownWrapper from './OperatoryDropdownWrapper/OperatoryDropdownWrapper';
import CopyTimeSlot from 'pages/OfficeSchedulePage/DayTimeSlot/DefaultTimeView/CopyTimeSlot/CopyTimeSlot';
import { WeeklyTimeslot } from 'pages/OfficeSchedulePage/WrapperSchedulePage';

interface RegularWorkingHourProps {
  timeFrame: TimeFrame;
}

const RegularWorkingHour: FC<RegularWorkingHourProps> = ({
  timeFrame: { day, timeSlots },
}) => {
  const isChecked = timeSlots.some((slot) => slot.start && slot.end);

  const { watch, setValue } = useFormContext<TimeFrameFormValues>();

  const timeFrames = watch('timeFrames');

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    const start = DEFAULT_WORKING_HOUR.START;
    const end = DEFAULT_WORKING_HOUR.END;
    const updatedTimeFrame = timeFrames.map((item) =>
      item.day === day
        ? {
            ...item,
            timeSlots: isChecked ? [{ start, end, operatories: [] }] : [],
          }
        : item
    );
    setValue('timeFrames', updatedTimeFrame, { shouldValidate: true });
  };

  const onAddTimeSlot = () => {
    const newTimeSlot = {
      start: DEFAULT_WORKING_HOUR.START,
      end: DEFAULT_WORKING_HOUR.END,
      operatories: [],
    };

    const updatedTimeFrame = timeFrames.map((item) =>
      item.day === day
        ? {
            ...item,
            timeSlots: [...item.timeSlots, newTimeSlot],
          }
        : item
    );

    setValue('timeFrames', updatedTimeFrame, { shouldValidate: true });
  };

  const onDeleteTimeSlot = (index: number) => {
    const updatedTimeSlots = timeSlots.filter((_, i) => i !== index);

    const updatedTimeFrame = timeFrames.map((item) =>
      item.day === day
        ? {
            ...item,
            timeSlots: updatedTimeSlots,
          }
        : item
    );
    setValue('timeFrames', updatedTimeFrame, { shouldValidate: true });
  };

  const onTimeChange = (
    day: string,
    value: string,
    type: string,
    index: number
  ) => {
    const updatedTimeSlots = timeSlots.map((slot, i) =>
      i === index ? { ...slot, [type]: value } : slot
    );

    const updatedTimeFrame = timeFrames.map((item) =>
      item.day === day
        ? {
            ...item,
            timeSlots: updatedTimeSlots,
          }
        : item
    );

    setValue('timeFrames', updatedTimeFrame, { shouldValidate: true });
  };

  const onOperatoryUpdated = (newValue: any[], index: number) => {
    const updatedTimeSlots = timeSlots.map((slot, i) =>
      i === index ? { ...slot, operatories: newValue } : slot
    );

    const updatedTimeFrame = timeFrames.map((item) =>
      item.day === day
        ? {
            ...item,
            timeSlots: updatedTimeSlots,
          }
        : item
    );

    setValue('timeFrames', updatedTimeFrame, { shouldValidate: true });
  };

  const onCopy = (copiedTimeSlots: WeeklyTimeslot[]) => {
    const updatedCopiedTimeSlotsFormat = copiedTimeSlots as unknown as [
      {
        checked: boolean;
        day: string;
        timeSlots: {
          start: string;
          end: string;
          operatories: any[];
        }[];
      },
    ];

    const root = timeFrames.find((timeFrame) => timeFrame.day === day)!;

    const appliedCopyTimeSlots = updatedCopiedTimeSlotsFormat.filter(
      (item) => item.checked
    );

    const updatedTimeFrames = timeFrames.map((timeFrame) => {
      const isCopiedApplied = appliedCopyTimeSlots.some(
        (item) => item.day === timeFrame.day
      );

      if (isCopiedApplied) {
        return {
          ...timeFrame,
          timeSlots: root.timeSlots,
        };
      }

      return timeFrame;
    });

    setValue('timeFrames', updatedTimeFrames, { shouldValidate: true });
  };

  return (
    <div
      key={day}
      className={`min-h-[6.4rem] border-b border-light-grey flex items-center`}
    >
      <div className="px-2 py-1 flex-1">
        <div className="flex justify-between">
          <div className={`flex gap-x-1`}>
            <input
              id={`day-${day}`}
              type="checkbox"
              className="w-2.2 h-2.2"
              checked={isChecked}
              onChange={onChange}
            />
            <label htmlFor={`day-${day}`} className="font-bold w-[3.1rem]">
              {capitalize(day)}
            </label>
            {!isChecked && <span>Unavailable</span>}
          </div>
          {isChecked && (
            <CopyTimeSlot
              day={day}
              start={timeSlots[0]?.start ?? DEFAULT_WORKING_HOUR.START}
              end={timeSlots[0]?.end ?? DEFAULT_WORKING_HOUR.END}
              onCopy={onCopy}
              timeSlots={timeFrames.map((timeFrame) => ({
                day: timeFrame.day,
                start: timeFrame.timeSlots[0]?.start,
                end: timeFrame.timeSlots[0]?.end,
                timeSlots: timeFrame.timeSlots,
              }))}
            />
          )}
        </div>

        {isChecked && (
          <div className="flex flex-col gap-y-2 pl-3.4 mt-1.4">
            {timeSlots.map((timeSlot, index) => (
              <div key={index} className="flex gap-x-0.8">
                <div className="border border-[#F5F5F5] flex-1 bg-[#fcfcfc] min-h-[15.6rem] rounded-[1.2rem] p-1.6">
                  <div className="flex gap-x-1.6 items-center">
                    <div className="flex flex-1 items-center">
                      <div className="flex-1 relative">
                        <SelectTime
                          type={'start'}
                          day={day}
                          value={timeSlot.start!}
                          end={timeSlot.end!}
                          onChange={(...params) =>
                            onTimeChange(...params, index)
                          }
                          buttonClassName="!w-full !h-4"
                          dropdownClassName="!w-full items-center"
                        />
                      </div>

                      <span className="mx-0.8">-</span>

                      <div className="flex-1 relative">
                        <SelectTime
                          type={'end'}
                          day={day}
                          value={timeSlot.end!}
                          start={timeSlot.start!}
                          onChange={(...params) =>
                            onTimeChange(...params, index)
                          }
                          buttonClassName="!w-full !h-4"
                          dropdownClassName="!w-full items-center"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-1.6">
                    <p className="mb-0.8">Operatories</p>
                    <OperatoryDropdownWrapper
                      onUpdate={(newValue) =>
                        onOperatoryUpdated(newValue, index)
                      }
                      value={timeSlot.operatories}
                    />
                  </div>
                </div>
                <button
                  className="self-start"
                  onClick={() => onDeleteTimeSlot(index)}
                >
                  <ClearIcon className="w-1.6 h-1.6" />
                </button>
              </div>
            ))}
            <button
              className="text-magenta font-bold mt-1.2"
              onClick={onAddTimeSlot}
            >
              + Add another
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegularWorkingHour;
